/**
 * src/domain/attention/ad-escrow-ledger.ts
 *
 * Ad Escrow Smart Contract & Revenue Distribution Ledger.
 * Handles:
 * - Campaign budget escrow locking and atomic balance decrements.
 * - 70/30 Creator Revenue Distribution (70% Creator, 15% Treasury, 15% Scam Insurance).
 * - Direct Attention Dividend Distribution in #sponsor-vault (60% Viewer, 25% Channel, 15% Insurance).
 * - 10-minute anti-farming rate-limit per DID.
 */

export interface AdCampaign {
  id: string;
  sponsorDid: string;
  sponsorName: string;
  headline: string;
  tagline: string;
  category: string;
  totalBudgetTokens: number;
  remainingBudgetTokens: number;
  costPerImpressionTokens: number;
  status: 'active' | 'exhausted' | 'paused';
  createdAt: number;
}

export interface ImpressionSettlement {
  id: string;
  campaignId: string;
  channelId: string;
  recipientDid: string;
  isVaultDirectClaim: boolean;
  totalCostTokens: number;
  distribution: {
    creatorTokens: number;
    viewerTokens: number;
    treasuryTokens: number;
    insurancePoolTokens: number;
  };
  settledAt: number;
}

export class AdEscrowLedger {
  public static readonly CLAIM_COOLDOWN_MS = 10 * 60 * 1000; // 10 minutes
  public static readonly MAX_HOURLY_TOKENS_PER_DID = 6;

  private static instance: AdEscrowLedger;
  private campaigns: Map<string, AdCampaign> = new Map();
  private settlements: ImpressionSettlement[] = [];
  private lastClaimByDid: Map<string, number> = new Map(); // did -> timestamp

  private constructor() {
    this.seedDefaultCampaigns();
  }

  public static getInstance(): AdEscrowLedger {
    if (!AdEscrowLedger.instance) {
      AdEscrowLedger.instance = new AdEscrowLedger();
    }
    return AdEscrowLedger.instance;
  }

  /**
   * Registers a new sponsored ad campaign with escrowed budget.
   */
  public createCampaign(params: {
    sponsorDid: string;
    sponsorName: string;
    headline: string;
    tagline: string;
    category: string;
    budgetTokens: number;
    costPerImpressionTokens: number;
  }): AdCampaign {
    if (params.budgetTokens <= 0) {
      throw new Error('Campaign budget must be strictly positive.');
    }
    if (params.costPerImpressionTokens <= 0 || params.costPerImpressionTokens > params.budgetTokens) {
      throw new Error('Invalid cost per impression.');
    }

    const campaign: AdCampaign = {
      id: `camp_${crypto.randomUUID()}`,
      sponsorDid: params.sponsorDid,
      sponsorName: params.sponsorName,
      headline: params.headline,
      tagline: params.tagline,
      category: params.category,
      totalBudgetTokens: params.budgetTokens,
      remainingBudgetTokens: params.budgetTokens,
      costPerImpressionTokens: params.costPerImpressionTokens,
      status: 'active',
      createdAt: Date.now(),
    };

    this.campaigns.set(campaign.id, campaign);
    return campaign;
  }

  /**
   * Settles a verified Human Pulse impression according to distribution policy:
   * - In Standard Channel: 70% Creator, 15% Treasury, 15% Scam Insurance Pool.
   * - In #sponsor-vault (Direct): 60% Viewer, 25% Channel, 15% Scam Insurance Pool.
   */
  public settleImpression(params: {
    campaignId: string;
    channelId: string;
    creatorDid: string;
    viewerDid: string;
    isVaultDirectClaim?: boolean;
    currentTimestamp?: number;
  }): ImpressionSettlement {
    const { campaignId, channelId, creatorDid, viewerDid, isVaultDirectClaim = false } = params;
    const now = params.currentTimestamp ?? Date.now();

    const campaign = this.campaigns.get(campaignId);
    if (!campaign) {
      throw new Error('Unknown ad campaign.');
    }
    if (campaign.status !== 'active') {
      throw new Error(`Campaign is ${campaign.status}.`);
    }

    const cpi = campaign.costPerImpressionTokens;
    if (campaign.remainingBudgetTokens < cpi) {
      campaign.status = 'exhausted';
      throw new Error('Campaign escrow budget exhausted.');
    }

    // Enforce 10-minute rate-limit per viewer DID
    const lastClaim = this.lastClaimByDid.get(viewerDid);
    if (lastClaim && now - lastClaim < AdEscrowLedger.CLAIM_COOLDOWN_MS) {
      const remainingMs = AdEscrowLedger.CLAIM_COOLDOWN_MS - (now - lastClaim);
      const remainingMin = Math.ceil(remainingMs / 60000);
      throw new Error(`Attention dividend rate-limit active. Please wait ${remainingMin}m before claiming again.`);
    }

    // Atomically decrement budget
    campaign.remainingBudgetTokens = Math.round((campaign.remainingBudgetTokens - cpi) * 100) / 100;
    if (campaign.remainingBudgetTokens <= 0) {
      campaign.status = 'exhausted';
    }

    // Compute distribution breakdown with rounding conservation
    let distribution: ImpressionSettlement['distribution'];

    if (isVaultDirectClaim) {
      // #sponsor-vault direct attention payout: 60% Viewer, 25% Channel, 15% Insurance
      const viewerTokens = Math.round(cpi * 0.60 * 100) / 100;
      const creatorTokens = Math.round(cpi * 0.25 * 100) / 100;
      const insurancePoolTokens = Math.round((cpi - viewerTokens - creatorTokens) * 100) / 100;

      distribution = {
        viewerTokens,
        creatorTokens,
        treasuryTokens: 0,
        insurancePoolTokens,
      };
    } else {
      // Standard channel impression: 70% Creator, 15% Treasury, 15% Insurance
      const creatorTokens = Math.round(cpi * 0.70 * 100) / 100;
      const treasuryTokens = Math.round(cpi * 0.15 * 100) / 100;
      const insurancePoolTokens = Math.round((cpi - creatorTokens - treasuryTokens) * 100) / 100;

      distribution = {
        viewerTokens: 0,
        creatorTokens,
        treasuryTokens,
        insurancePoolTokens,
      };
    }

    // Record rate-limit timestamp for viewer
    this.lastClaimByDid.set(viewerDid, now);

    const settlement: ImpressionSettlement = {
      id: `stl_${crypto.randomUUID()}`,
      campaignId,
      channelId,
      recipientDid: isVaultDirectClaim ? viewerDid : creatorDid,
      isVaultDirectClaim,
      totalCostTokens: cpi,
      distribution,
      settledAt: now,
    };

    this.settlements.push(settlement);
    return settlement;
  }

  public getCampaign(id: string): AdCampaign | undefined {
    return this.campaigns.get(id);
  }

  public getAllActiveCampaigns(): AdCampaign[] {
    return Array.from(this.campaigns.values()).filter((c) => c.status === 'active');
  }

  public getSettlements(): ImpressionSettlement[] {
    return [...this.settlements];
  }

  public getRemainingCooldownMs(did: string, currentTimestamp?: number): number {
    const now = currentTimestamp ?? Date.now();
    const lastClaim = this.lastClaimByDid.get(did);
    if (!lastClaim) return 0;
    const elapsed = now - lastClaim;
    return Math.max(0, AdEscrowLedger.CLAIM_COOLDOWN_MS - elapsed);
  }

  public clear(): void {
    this.campaigns.clear();
    this.settlements = [];
    this.lastClaimByDid.clear();
    this.seedDefaultCampaigns();
  }

  private seedDefaultCampaigns(): void {
    this.createCampaign({
      sponsorDid: 'did:key:z6MksponsorRelay',
      sponsorName: 'Sovereign Edge Relay',
      headline: 'Deploy a Mesh Relay Node in Under 60 Seconds',
      tagline: 'Earn 15 tokens daily by routing encrypted P2P traffic on Raspberry Pi or ESP32.',
      category: 'tech-depin',
      budgetTokens: 500,
      costPerImpressionTokens: 4.0,
    });

    this.createCampaign({
      sponsorDid: 'did:key:z6MksponsorAudio',
      sponsorName: 'Kuala Lumpur Sound Labs',
      headline: 'Analog Synthesizer Presets & Acoustic Impulse Responses',
      tagline: 'Royalty-free sounds crafted on vintage Roland & Moog circuitry.',
      category: 'creators-media',
      budgetTokens: 300,
      costPerImpressionTokens: 2.0,
    });

    this.createCampaign({
      sponsorDid: 'did:key:z6MksponsorHardware',
      sponsorName: 'YubiKey & NitroKey Sovereign Bundles',
      headline: 'Hardware FIDO2 Passkeys with Hardware PIN Attestation',
      tagline: 'Zero passwords, zero phishing vulnerabilities. Official mesh partner.',
      category: 'tech-depin',
      budgetTokens: 400,
      costPerImpressionTokens: 3.0,
    });
  }
}

export const adEscrowLedger = AdEscrowLedger.getInstance();
